export enum RbacRoleGremlinInternalCompany {
  ADMIN = 'ADMIN',
  DEV = 'DEV',
  BOOTCAMP_ORGANIZER = 'BOOTCAMP_ORGANIZER'
}

export enum RbacRoleCompany {
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  COMPANY_COORDINATOR = 'COMPANY_COORDINATOR',
  COMPANY_MANAGER = 'COMPANY_MANAGER',
  COMPANY_OWNER = 'COMPANY_OWNER',
  COMPANY_USER = 'COMPANY_USER',
  DEFAULT = 'DEFAULT' // this is an alias on the BE, but this is a value we will receive in the response and is a valid value we can send in the request - see libraries/core/src/main/java/com/gremlininc/auth/models/Role.java
}

export enum RbacRoleTeam {
  TEAM_CREDENTIAL_MANAGER = 'TEAM_CREDENTIAL_MANAGER',
  TEAM_MANAGER = 'TEAM_MANAGER',
  TEAM_USER = 'TEAM_USER',
  TEAM_VIEWER = 'TEAM_VIEWER',
  DEFAULT = 'DEFAULT' // this is an alias on the BE, but this is a value we will receive in the response and is a valid value we can send in the request - see libraries/core/src/main/java/com/gremlininc/auth/models/Role.java
}

export type RbacRolesAll = string // this has the type of RbacRoleGremlinInternalCompany | RbacRoleCompany | RbacRoleTeam - or string when it is a custom role

/**
 * Note: not every category needs to be represented here! Only the ones the webapp uses.
 * When/if that's the case, it can be copied here from the service codebase:
 * libraries/core/src/main/java/com/gremlininc/auth/models/Privilege.java
 */
export enum RbacPrivilegeItemCategory {
  COMPANY_USERS = 'COMPANY_USERS',
  INTEGRATIONS = 'INTEGRATIONS',
  REQUIRED = 'REQUIRED',
  TEAMS = 'TEAMS'
}

/**
 * Note: not every privilege needs to be represented here! Only the ones the webapp uses.
 * When/if that's the case, it can be copied here from the service codebase:
 * libraries/core/src/main/java/com/gremlininc/auth/models/Privilege.java
 */
export enum RbacPrivilege {
  // Company
  MINIMUM_COMPANY_PRIVILEGES = 'MINIMUM_COMPANY_PRIVILEGES',
  PROMOTE_DEMOTE_OWNER = 'PROMOTE_DEMOTE_OWNER',
  ALL_API_KEYS_READ = 'ALL_API_KEYS_READ',
  API_KEYS_READ = 'API_KEYS_READ',
  API_KEYS_WRITE = 'API_KEYS_WRITE',
  COMPANIES_READ = 'COMPANIES_READ',
  COMPANY_INTEGRATIONS_READ = 'COMPANY_INTEGRATIONS_READ',
  COMPANY_INTEGRATIONS_WRITE = 'COMPANY_INTEGRATIONS_WRITE',
  RELIABILITY_REPORTS_READ = 'RELIABILITY_REPORTS_READ',
  REPORTS_READ = 'REPORTS_READ',
  SCENARIOS_READ = 'SCENARIOS_READ',
  SCENARIO_SHARE_WRITE = 'SCENARIO_SHARE_WRITE',
  SERVICES_READ = 'SERVICES_READ',
  TEAMS_READ = 'TEAMS_READ',
  TEST_SUITES_READ = 'TEST_SUITES_READ',

  // Team
  MINIMUM_TEAM_PRIVILEGES = 'MINIMUM_TEAM_PRIVILEGES',
  CLIENTS_READ = 'CLIENTS_READ',
  CLIENTS_WRITE = 'CLIENTS_WRITE',
  TEAM_SECURITY_READ = 'TEAM_SECURITY_READ',
  TEAM_SECURITY_WRITE = 'TEAM_SECURITY_WRITE',

  // Internal privileges
  ADMIN = 'ADMIN',
  DEV = 'DEV',
  HALT_ALL = 'HALT_ALL', // All users have this. We don't display this for security reasons - it can't be unset by any user
  MANAGE_BOOTCAMPS = 'MANAGE_BOOTCAMPS'
}

export interface RbacRoleItem {
  description?: string
  privileges: RbacPrivilege[]
}

export enum RbacRoleItemType {
  COMPANY = 'Company',
  TEAM = 'Team'
}

export interface RbacRoleCompanyItem extends RbacRoleItem {
  key: RbacRoleCompany | RbacRoleGremlinInternalCompany
  name: RbacRoleCompany | RbacRoleGremlinInternalCompany
  type: RbacRoleItemType.COMPANY
}

export interface RbacCustomRoleCompanyItem extends RbacRoleItem {
  name: string
  key: string
  type: RbacRoleItemType.COMPANY
}

export interface RbacRoleTeamItem extends RbacRoleItem {
  key: RbacRoleTeam
  name: RbacRoleTeam
  type: RbacRoleItemType.TEAM
}

export interface RbacCustomRoleTeamItem extends RbacRoleItem {
  key: string
  name: string
  type: RbacRoleItemType.TEAM
}

export interface RbacPrivilegeItem {
  key: RbacPrivilege
  description: string
  category: RbacPrivilegeItemCategory
}

export type CompanyRoles = Partial<
  Record<RbacRoleCompany | RbacRoleGremlinInternalCompany, RbacRoleCompanyItem>
>

export type CustomCompanyRoles = Record<string, RbacCustomRoleCompanyItem>

export type TeamRoles = Record<RbacRoleTeam, RbacRoleTeamItem>

export type CustomTeamRoles = Record<string, RbacCustomRoleTeamItem>

export interface RbacBody {
  roles: {
    company: CompanyRoles
    team: TeamRoles
  }
  customRoles: {
    company: CustomCompanyRoles
    team: CustomTeamRoles
  }
  privileges: {
    company: Record<RbacPrivilege, RbacPrivilegeItem>
    team: Record<RbacPrivilege, RbacPrivilegeItem>
  }
}
